import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { getChildActivities, getParentChildActivities, type ChildActivity } from '../../services/parentService';

interface UseParentActivityTimelineParams {
  studentId?: string;
  maxItems?: number;
  dateRange?: {
    start: Date;
    end: Date;
  };
  activityTypeFilter?: string;
  dateFilter?: [Date | null, Date | null];
}

// Transform parent activity to detection-like format for consistent rendering
const transformParentActivityToDetection = (activity: any, studentId?: string): any => {
  // Handle both simple ChildActivity and complex ActivitySerializer response
  const isSerializedActivity = activity.detected_at || activity.activity_info || activity.location?.area;

  let timestamp, activityType, confidence, description, location, verification, media;

  if (isSerializedActivity) {
    // Handle ActivitySerializer response structure
    timestamp = activity.detected_at || activity.time_context?.detected_at || new Date().toISOString();
    activityType = activity.activity_info?.value || activity.activity_type || 'unknown';
    confidence = activity.confidence || activity.processing?.confidence || 0.75;
    description = activity.description || activity.activity_info?.label || `${activityType} activity`;

    // Extract location information from nested structure
    location = {
      area_name: activity.location?.area?.name || 'Classroom',
      camera_name: activity.location?.camera?.name || 'Classroom Camera',
      area_id: activity.location?.area?.id,
      camera_id: activity.location?.camera?.id,
    };

    // Extract verification information
    verification = {
      status: activity.verification?.status?.value || (activity.verified ? 'verified' : 'pending'),
      notes: activity.verification?.notes || '',
      verified_at: activity.verification?.verified_at,
    };

    // Extract media information
    media = {
      image_url: activity.media?.thumbnail || activity.media?.snapshot,
      video_url: activity.media?.video_clip,
    };
  } else {
    // Handle simple ChildActivity structure
    timestamp = activity.timestamp || new Date().toISOString();
    activityType = activity.activity_type || 'unknown';
    confidence = activity.confidence || 0.75;
    description = activity.description || activity.ai_description || `${activityType} activity`;

    location = {
      area_name: activity.location || 'Classroom',
      camera_name: 'Classroom Camera',
      area_id: null,
      camera_id: null,
    };

    verification = {
      status: activity.verified ? 'verified' : 'pending',
      notes: activity.teacher_notes || '',
      verified_at: activity.verified ? timestamp : null,
    };

    media = {
      image_url: activity.image_url,
      video_url: activity.video_url,
    };
  }

  // Parse and validate the timestamp
  const parsedDate = new Date(timestamp);
  const validTimestamp = isNaN(parsedDate.getTime()) ? new Date().toISOString() : timestamp;

  // Normalize confidence to 0-1 range
  const normalizedConfidence = confidence > 1 ? confidence / 100 : confidence;

  return {
    id: activity.id || `activity-${Date.now()}-${Math.random()}`,
    camera_id: location.camera_id || '',
    detection_type: 'action' as const,
    confidence: normalizedConfidence,
    detected_at: validTimestamp,
    bounding_box: activity.bounding_box || activity.location?.bounding_box || undefined,
    student_id: studentId || null,
    activity_type: activityType,
    verified: verification.status === 'verified',
    verified_at: verification.verified_at,

    // Create camera object structure that timeline expects
    camera: {
      id: location.camera_id || 'parent-camera',
      name: location.camera_name,
      area: {
        id: location.area_id || 'parent-area',
        name: location.area_name,
        area_type: 'classroom'
      }
    },

    metadata: {
      location: location.area_name,
      description: description,
      ai_description: activity.ai_description,
      teacher_notes: verification.notes,
      tags: activity.tags || [],
      image_url: media.image_url,
      video_url: media.video_url,
    },
    created_at: validTimestamp,

    // Legacy compatibility fields for timeline rendering
    timestamp: validTimestamp,
    verification_status: verification.status,
    verification_notes: verification.notes,
    safety_score: activity.safety?.score?.value || 85,
    location: location.area_name,
    description: description,
    image_url: media.image_url,
    video_url: media.video_url,
  };
};

export const useParentActivityTimeline = ({
  studentId,
  maxItems = 10,
  dateRange,
  activityTypeFilter,
  dateFilter,
}: UseParentActivityTimelineParams) => {
  // Determine date parameters
  const startDate = dateRange?.start || dateFilter?.[0];
  const endDate = dateRange?.end || dateFilter?.[1];

  // Use today's date if no date range specified
  const today = new Date().toISOString().split('T')[0];
  const queryStartDate = startDate ? startDate.toISOString().split('T')[0] : today;
  const queryEndDate = endDate ? endDate.toISOString().split('T')[0] : today;

  // Fetch activities using parent-specific endpoint
  const {
    data: activitiesData,
    isLoading,
    error,
    refetch,
  } = useQuery(
    ['parent-activity-timeline', studentId, queryStartDate, queryEndDate, maxItems, activityTypeFilter],
    async () => {
      if (!studentId) return { items: [] as ChildActivity[], pagination: null };

      try {
        // Use the appropriate endpoint based on date range
        if (queryStartDate === queryEndDate && queryStartDate === today) {
          // For today's activities, use the simpler endpoint
          const result = await getChildActivities(studentId, { date: queryStartDate });
          return result;
        } else {
          // For date ranges, use the more flexible endpoint
          const result = await getParentChildActivities(studentId, {
            start_date: queryStartDate,
            end_date: queryEndDate,
            per_page: maxItems,
          });
          return result;
        }
      } catch (error) {
        console.error('Error fetching parent activities:', error);
        return { items: [] as ChildActivity[], pagination: null };
      }
    },
    {
      enabled: !!studentId,
      staleTime: 1 * 60 * 1000, // 1 minute
      refetchInterval: 30 * 1000, // Refetch every 30 seconds for near real-time updates
    }
  );

  // Transform and filter activities
  const detections = useMemo(() => {
    const activities: ChildActivity[] = Array.isArray(activitiesData)
      ? activitiesData
      : (activitiesData?.items || []);

    // Debug logging to understand data structure
    if (activities.length > 0) {
      console.log('Parent Activity Timeline - Raw activity data:', activities[0]);
    }

    let filteredActivities: ChildActivity[] = activities;

    // Apply activity type filter
    if (activityTypeFilter) {
      filteredActivities = filteredActivities.filter(
        (activity: ChildActivity) => activity.activity_type === activityTypeFilter
      );
    }

    // Limit results
    if (maxItems) {
      filteredActivities = filteredActivities.slice(0, maxItems);
    }

    // Transform to detection format
    const transformedDetections = filteredActivities.map((activity: ChildActivity) => {
      const transformed = transformParentActivityToDetection(activity, studentId);

      // Debug logging for first item
      if (filteredActivities.indexOf(activity) === 0) {
        console.log('Parent Activity Timeline - Transformed detection:', {
          original: activity,
          transformed: transformed,
          camera: transformed.camera,
          timestamp: transformed.timestamp,
          confidence: transformed.confidence,
          verification_status: transformed.verification_status
        });
      }

      return transformed;
    });

    return transformedDetections;
  }, [activitiesData, activityTypeFilter, maxItems, studentId]);

  // Calculate activity statistics
  const stats = useMemo(() => {
    const totalActivities = detections.length;
    const activityTypes = detections.reduce((acc, detection) => {
      const type = detection.activity_type || 'unknown';
      acc[type] = (acc[type] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    const verifiedActivities = detections.filter(
      d => d.verification_status === 'verified'
    ).length;

    const averageConfidence = totalActivities > 0
      ? detections.reduce((sum, d) => sum + (d.confidence || 0), 0) / totalActivities
      : 0;

    return {
      totalActivities,
      activityTypes,
      verifiedActivities,
      averageConfidence,
      verificationRate: totalActivities > 0 ? (verifiedActivities / totalActivities) * 100 : 0,
    };
  }, [detections]);

  return {
    detections,
    isLoading,
    error,
    refetch,
    stats,
    isEmpty: detections.length === 0,
    hasMore: false, // For now, we don't implement pagination in timeline
  };
};

// Hook for real-time activity updates
export const useParentActivityTimelineRealtime = (params: UseParentActivityTimelineParams) => {
  const result = useParentActivityTimeline(params);

  // In a real implementation, this would connect to WebSocket for real-time updates
  // For now, we rely on the refetch interval

  return {
    ...result,
    isRealtime: true,
    lastUpdate: new Date(),
  };
};

// Hook for activity timeline with infinite scroll (for future use)
export const useParentActivityTimelineInfinite = (params: UseParentActivityTimelineParams) => {
  // This could be implemented using useInfiniteQuery for pagination
  // For now, return the basic timeline
  return useParentActivityTimeline(params);
};
